import { vocab } from '@/ds';
import type { Schemas } from '@/lib/api/client';
import { formatDuration, formatTimestamp, toInstant } from '@/lib/format/format';
import { retryOf } from '@/features/automation/vocab';
import { plural } from '@/features/organizer/vocab';

// A run in the design system's words: its status, trigger and RunSummary counts.

type Run = Schemas['ScrapeRunReportDto'];
type Spend = Schemas['SpendSummaryDto'];
type ProviderSpend = Schemas['ProviderSpendDto'];

export const RUN_STATUSES = ['running', 'succeeded', 'partially-failed', 'failed'] as const;
export const RUN_TRIGGERS = ['manual', 'scheduled', 'import'] as const;

export type RunStatus = (typeof RUN_STATUSES)[number];
export type RunTrigger = (typeof RUN_TRIGGERS)[number];

const STATUS: Record<string, RunStatus> = {
  Running: 'running',
  Succeeded: 'succeeded',
  PartiallyFailed: 'partially-failed',
  Failed: 'failed',
};

// CsvManual is an import by hand; the design system has one word for both.
const TRIGGER: Record<string, RunTrigger> = {
  Manual: 'manual',
  Scheduled: 'scheduled',
  Import: 'import',
  CsvManual: 'import',
};

const STATUS_TO_API: Record<RunStatus, string> = {
  running: 'Running',
  succeeded: 'Succeeded',
  'partially-failed': 'PartiallyFailed',
  failed: 'Failed',
};

// The importer records its runs as Import; CsvManual is in the API's vocabulary but never written.
const TRIGGER_TO_API: Record<RunTrigger, string> = {
  manual: 'Manual',
  scheduled: 'Scheduled',
  import: 'Import',
};

export const runStatus = (run: Run): RunStatus => STATUS[run.status] ?? 'failed';
export const runTrigger = (run: Run): RunTrigger => TRIGGER[run.trigger] ?? 'manual';
export const isImport = (run: Run): boolean => runTrigger(run) === 'import';
export const isRunning = (run: Run): boolean => run.status === 'Running';
/** Running, or finished with its rules still deciding: not yet landed. */
export const isLive = (run: Run): boolean => isRunning(run) || run.rulesPending;

export const IDLE_MS = 60_000;
/** How often to read again: a query's own rate while a run is live, the light rate otherwise. */
export const pollInterval = (live: boolean, liveMs: number | false, idleMs: number): number | false =>
  live ? liveMs : idleMs;

export const statusToApi = (status: RunStatus): string => STATUS_TO_API[status];
export const triggerToApi = (trigger: RunTrigger): string => TRIGGER_TO_API[trigger];

export const statusLabel = (status: RunStatus): string =>
  vocab.STATUS.run?.[status]?.label ?? status;
export const triggerLabel = (trigger: RunTrigger): string =>
  vocab.TRIGGERS[trigger]?.label ?? trigger;

/** A run's trigger in words: "Scheduled, retry 1 of 2" for a retry, else the trigger's word. */
export const triggerWords = (run: Run): string => {
  const label = triggerLabel(runTrigger(run));
  return run.retry
    ? `${label}, ${retryOf(run.retry)}`
    : label;
};

// The API's ExtractionValidator phrases; one missing here reads as a failure, never a rejection.
const CHECKS: [RegExp, (match: RegExpExecArray) => string][] = [
  [/^missing name$/, () => 'Missing name'],
  [/^missing date$/, () => 'Missing date'],
  [/^event is dated before today$/, () => 'Date in the past'],
  [/^event is dated beyond the eighteen-month horizon$/, () => 'Date more than 18 months out'],
  [/^race distance out of range: (.+?)\s*km$/, (m) => `Impossible distance (${m[1]} km)`],
  [/^race category outside the product's vocabulary$/, () => 'Unknown race category'],
  [/^organizer url is not an absolute web address$/, () => "Organizer link isn't a web address"],
  [/^organizer url is the same as the source page$/, () => 'Organizer link is the source page'],
  [
    /^organizer url carries a userinfo component ahead of its host$/,
    () => 'Organizer link hides its real address',
  ],
];

function checkWord(phrase: string): string | null {
  for (const [pattern, word] of CHECKS) {
    const match = pattern.exec(phrase);
    if (match) return word(match);
  }
  return null;
}

/** The checks behind "a; b", in the design system's words; null when any phrase isn't a check's. */
export function checkWords(reason: string): string[] | null {
  const words = reason.split(';').map((part) => checkWord(part.trim()));
  return words.every((word) => word !== null) ? (words as string[]) : null;
}

/** An import names no source: its entries can belong to several. */
export const IMPORT_SOURCE = 'Transfer file';

/** Event pages a run found: extracted, unchanged or held for a dateless event. */
export const eventsFound = (run: Run): number =>
  run.eventsExtracted + run.pagesUnchanged + run.monthOnlyDatesEncountered;

/**
 * A scrape lists each page its checks rejected as a failure too, so those are taken out. An
 * import counts its invalid entries as rejected without listing them.
 */
export const pagesFailed = (run: Run): number =>
  isImport(run)
    ? run.failures.length
    : Math.max(0, run.failures.length - run.eventsFailedValidation);

/** "1 min 17 s" once it has landed; "3 min so far" while it runs, so it doesn't tick by the second. */
export function runDuration(run: Run, now = new Date()): string {
  const started = toInstant(run.startedAt).getTime();
  if (run.finishedAt) return formatDuration(toInstant(run.finishedAt).getTime() - started);
  const minutes = Math.floor((now.getTime() - started) / 60_000);
  return minutes < 1 ? 'under a minute so far' : `${formatDuration(minutes * 60_000)} so far`;
}

/** RunSummary's props for a run. */
export function summaryOf(run: Run, source: string, now = new Date()) {
  return {
    source,
    status: runStatus(run),
    halted: run.spendCeilingReached,
    stopped: !!run.stoppedByPause,
    trigger: runTrigger(run),
    started: formatTimestamp(run.startedAt, now),
    duration: runDuration(run, now),
    counts: {
      fetched: run.pagesFetched,
      unchanged: run.pagesUnchanged,
      extracted: run.eventsExtracted,
      rejected: run.eventsFailedValidation,
      failed: pagesFailed(run),
    },
    cost: run.estimatedCostUsd,
    tokens: run.llmInputTokens + run.llmOutputTokens,
  };
}

/** "last 30 days": the window the API judges spend over. */
export const spendPeriod = (spend: { windowDays: number }): string =>
  spend.windowDays === 1 ? 'last day' : `last ${spend.windowDays} days`;

/** The active provider's reading; the summary's own figures are the same ones. */
export const activeSpend = (spend: Spend): ProviderSpend =>
  spend.providers.find((p) => p.isActive) ?? {
    ...spend,
    provider: spend.activeProvider,
    isActive: true,
  };

/** One provider's reading, whether or not it is the one in use. */
export const readingOf = (
  spend: Spend,
  provider: string | null | undefined,
): ProviderSpend | undefined => spend.providers.find((p) => p.provider === provider);

/** SpendMeter's figures for one provider, straight from the API's reading. */
export const spendMeter = (reading: ProviderSpend, summary: { windowDays: number }) => ({
  spent: reading.spentUsd,
  alert: reading.alertThresholdUsd,
  ceiling: reading.ceilingUsd,
  period: spendPeriod(summary),
  label: `AI spend on ${reading.provider}, ${spendPeriod(summary)}`,
});

/** The gap in a meter's place when calls recorded no cost: never a zero. */
export const NOT_RECORDED = 'Not recorded';

export const noCostDetail = (reading: ProviderSpend): string =>
  `${plural(reading.llmCalls, 'AI call')} on ${reading.provider} recorded no cost`;

/** When a limit lifts, with the one caveat that goes with it. */
export const clearsAfter = (at: string, now: Date): string =>
  `after ${formatTimestamp(at, now)} if nothing more is spent`;

/** A provider at its ceiling: its runs are refused, and when they can start again. */
export const ceilingRefusal = (provider: string, reading: ProviderSpend, now: Date): string =>
  reading.ceilingClearsAt
    ? `Runs on ${provider} are refused. They can start again ${clearsAfter(reading.ceilingClearsAt, now)}.`
    : `Runs on ${provider} are refused: its spend ceiling is reached.`;

/** Where the ceiling stands today for a halted run's own provider; generic when that reading is missing. */
export function ceilingNow(spend: Spend | undefined, provider: string | null, now: Date): string {
  const reading = spend && provider ? readingOf(spend, provider) : undefined;
  if (!provider || !reading)
    return `New runs${provider ? ` on ${provider}` : ''} are refused while spend is at the ceiling.`;
  return reading.ceilingReached
    ? ceilingRefusal(provider, reading, now)
    : `Spend has since fallen below the ceiling, so runs on ${provider} can start again.`;
}

/** Which pause stopped a run: its source's, or all automation's (a scheduled run only). */
export const stoppedBy = (run: Run): 'source' | 'automation' | null =>
  run.stoppedByPause === 'Source'
    ? 'source'
    : run.stoppedByPause === 'Automation'
      ? 'automation'
      : null;
